import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface TreeNode {
  name: string;
  path: string;
  type: 'folder' | 'file';
  children?: TreeNode[];
  size?: number;
  mtime?: string;
}

export interface ObsidianFileDTO {
  path: string;
  name: string;
  folder: string;
  size: number;
  mtime: string;
  raw: string;
  descricao: string | null;
  tags: string[];
  wikilinks: string[];
  frontmatter: Record<string, string | string[]>;
  headings: string[];
}

export interface FileResponse {
  file: ObsidianFileDTO;
  item_id: number | null;
  backlinks: Array<{
    id: number;
    source_item_id: number;
    source_nome: string;
    source_tipo: string;
    tipo_relacao: string;
  }>;
  outgoing: Array<{
    id: number;
    target_item_id: number;
    target_nome: string;
    target_tipo: string;
    tipo_relacao: string;
  }>;
  counts: { backlinks: number; outgoing: number };
}

export interface GitCommit {
  sha: string;
  shaShort: string;
  author: string;
  date: string;
  message: string;
}

export interface GitStatusResult {
  available: boolean;
  clean?: boolean;
  ahead?: number;
  behind?: number;
  branch?: string | null;
  uncommitted?: number;
}

export function useVaultTree(): UseQueryResult<TreeNode> {
  return useQuery({
    queryKey: ['obsidian', 'tree'],
    queryFn: () => api<TreeNode>('/api/obsidian/tree'),
    staleTime: 60_000,
  });
}

export function useObsidianFile(path: string | null): UseQueryResult<FileResponse> {
  return useQuery({
    queryKey: ['obsidian', 'file', path],
    queryFn: () => api<FileResponse>(`/api/obsidian/file?path=${encodeURIComponent(path ?? '')}`),
    enabled: path !== null && path !== '',
  });
}

export function useObsidianTags(): UseQueryResult<{ tags: Array<{ tag: string; count: number }> }> {
  return useQuery({
    queryKey: ['obsidian', 'tags'],
    queryFn: () => api<{ tags: Array<{ tag: string; count: number }> }>('/api/obsidian/tags'),
    staleTime: 60_000,
  });
}

export function useGitLog(path: string | null): UseQueryResult<{ commits: GitCommit[]; total: number }> {
  return useQuery({
    queryKey: ['obsidian', 'git', 'log', path],
    queryFn: () => api<{ commits: GitCommit[]; total: number }>(
      `/api/obsidian/git/log?path=${encodeURIComponent(path ?? '')}`,
    ),
    enabled: path !== null && path !== '',
  });
}

export function useGitStatus(): UseQueryResult<GitStatusResult> {
  return useQuery({
    queryKey: ['obsidian', 'git', 'status'],
    queryFn: () => api<GitStatusResult>('/api/obsidian/git/status'),
    refetchInterval: 300_000,
  });
}
